// Tiny client-side router: history API + useSyncExternalStore. No dependencies.

import { useEffect, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from 'react';

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function subscribe(l: () => void) {
  listeners.add(l);
  window.addEventListener('popstate', l);
  return () => {
    listeners.delete(l);
    window.removeEventListener('popstate', l);
  };
}

const snapshot = () => location.pathname + location.search;

/** Current `pathname + search`; re-renders on navigation. */
export function useLocation(): { path: string; search: URLSearchParams } {
  const loc = useSyncExternalStore(subscribe, snapshot, snapshot);
  const i = loc.indexOf('?');
  return { path: normalize(i < 0 ? loc : loc.slice(0, i)), search: new URLSearchParams(i < 0 ? '' : loc.slice(i)) };
}

function normalize(p: string) {
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
}

/** Workspace route: always entered and left with a full page load (it owns global terminal state).
 * /preview is served by the hub (e.g. after signing in from `cmx open`). */
const isHardRoute = (to: string) => to === '/app' || to.startsWith('/app?') || to.startsWith('/app#') || to.startsWith('/preview?');

export function navigate(to: string, opts: { replace?: boolean } = {}) {
  if (isHardRoute(to) || location.pathname === '/app') {
    if (opts.replace) location.replace(to);
    else location.assign(to);
    return;
  }
  const [pathAndSearch, hash] = to.split('#');
  if (opts.replace) history.replaceState(null, '', to);
  else history.pushState(null, '', to);
  emit();
  if (hash) requestAnimationFrame(() => document.getElementById(hash)?.scrollIntoView());
  else if (pathAndSearch) window.scrollTo(0, 0);
}

/** Only allow same-origin relative targets for ?next= redirects. */
export function safeNext(v: string | null, fallback: string) {
  return v && v.startsWith('/') && !v.startsWith('//') && !v.startsWith('/\\') ? v : fallback;
}

export function Link({ to, onClick, ...rest }: { to: string } & AnchorHTMLAttributes<HTMLAnchorElement>) {
  const click = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (rest.target && rest.target !== '_self') return;
    // Same-page anchors: let the browser scroll.
    if (to.startsWith('#')) return;
    e.preventDefault();
    navigate(to);
  };
  return <a href={to} onClick={click} {...rest} />;
}

export function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(to, { replace: true }), [to]);
  return null;
}
